<template>
  <v-hover v-slot="{ hover }">
    <v-data-table
      :class="{ 'on-hover': hover }"
      dense
      disable-pagination
      fixed-header
      :headers="tableColumns"
      hide-default-footer
      :items="borrowerLocates"
      sort-by="id"
      sort-desc
    >
      <template #[`item.checkoff`]="{ item }">
        <v-checkbox color="secondary" dense hide-details @click="checkOff(item)"></v-checkbox>
      </template>

      <template #[`item.createdAt`]="{ item }">
        <pretty-date-time :when="item.createdAt"></pretty-date-time>
      </template>

      <template #[`item.quantity`]="{ item }">
        <pretty-number :value="item.quantity" />
      </template>

      <template #[`item.approvedQuantity`]="{ item }">
        <pretty-number :value="item.approvedQuantity" />
      </template>

      <template #[`item.category`]="{ item }">
        <span>{{ item.security.id % 2 == 0 ? 'GC' : 'HTB' }}</span>
      </template>

      <template #[`item.refresh`]="{ item }">
        <v-btn color="secondary" x-small @click="locateAgain(item)">
          <v-icon left x-small> mdi-refresh</v-icon>
          Locate
        </v-btn>
      </template>
    </v-data-table>
  </v-hover>
</template>

<script setup lang="ts">
import i18n from '@/localisation/i18n';
import { i18nServerMessage } from '@/utils/helpers/rest-response';
import { BorrowIntent } from '@/utils/api/locates';
import { DataTableHeader } from 'vuetify';
import PrettyDateTime from '@/modules/common/components/format-date/PrettyDateTime.vue';
import { BorrowerLocate } from '@/modules/borrower/models';
import { useBorrowerLocates } from '@/modules/borrower/composables';
import { useSnackbarManager } from '@/modules/snackbar/services/snackbar-manager';

defineProps<{
  borrowerLocates: BorrowerLocate[];
}>();

const emit = defineEmits<{
  (e: 'locate-intent', intent: BorrowIntent): void;
}>();

const snackbar = useSnackbarManager();

const tableColumns: DataTableHeader[] = [
  {
    text: i18n.t('Counterparty') as string,
    value: 'security.name',
    class: 'text-truncate',
    cellClass: 'text-truncate',
  },
  {
    text: i18n.t('Date') as string,
    value: 'createdAt',
    class: 'text-truncate',
    cellClass: 'text-truncate',
  },
  {
    text: i18n.t('Requested Qty') as string,
    value: 'quantity',
    class: 'text-truncate',
    cellClass: 'text-truncate is-numeric',
    align: 'end',
  },
  {
    text: i18n.t('Approved Qty') as string,
    value: 'approvedQuantity',
    class: 'text-truncate',
    cellClass: 'text-truncate is-numeric',
    align: 'end',
  },

  {
    text: 'Liquidity Cat.',
    value: 'category',
    class: 'text-truncate',
    cellClass: 'text-truncate category',
    align: 'center',
  },
  {
    text: i18n.t('Refresh') as string,
    value: 'refresh',
    class: 'text-truncate',
    cellClass: 'text-truncate',
    align: 'center',
  },
];

async function checkOff(locate: BorrowerLocate): Promise<void> {
  if (locate.security == null) {
    return;
  }

  try {
    const { checkOffLocate } = useBorrowerLocates();
    await checkOffLocate(locate);

    snackbar.confirm(i18n.t('checkedOffLocate') as string);
  } catch (e) {
    snackbar.error(i18nServerMessage(e, `Locate ${locate.security.id}' cannot be checked off`));
  }
}

function locateAgain(locate: BorrowerLocate): void {
  emit('locate-intent', {
    security: locate.security,
    quantity: locate.quantity,
  } as BorrowIntent);
}
</script>

<style lang="scss" scoped></style>
